// useAddressAutocomplete.js
//
// Requires "Places API (New)" in GCP Console.
// Never uses AutocompleteService / PlacesService (those need the legacy "Places API").
//
// ── WHY no `types` or `includedPrimaryTypes` ────────────────────────────────
//   • `types` is a legacy-only field → InvalidValueError on the new API.
//   • `includedPrimaryTypes` accepts only establishment/POI type strings from
//     Table A of Google's place-types list.  Geocoding types like `street_address`
//     or `geocode` are NOT valid here and also throw InvalidValueError.
//   • Solution: omit both fields entirely.  `includedRegionCodes` handles
//     geographic restriction; address suggestions dominate naturally.
//
// ── WHY city → ZIP fill is removed ──────────────────────────────────────────
//   Geocoding a *city name* returns a locality result which has no `postal_code`
//   component (a city spans many postal codes).  Promising ZIP from city input
//   would set an incorrect/random ZIP.  Only state is reliably derivable.
//
// ── WHY suggestion click previously didn't fill ─────────────────────────────
//   AutocompleteSuggestion can return both placePredictions AND queryPredictions.
//   queryPredictions have `placePrediction === null`.  Calling `.toPlace()` on
//   null threw a JS error that was silently swallowed, leaving all fields empty.
//   Fix: filter to place-only suggestions + fill address1 immediately from
//   mainText before the async fetchFields resolves.

import { useState, useEffect, useRef, useCallback } from 'react';

// ── Address-component parsers ────────────────────────────────────────────────

// NEW Places API format: longText / shortText
const parseNewComponents = (components, countryHint) => {
  const get = (type, useShort = false) => {
    const c = components.find((x) => x.types.includes(type));
    return c ? (useShort ? c.shortText : c.longText) : '';
  };

  const address1 = [get('street_number'), get('route')].filter(Boolean).join(' ');

  const city =
    get('locality') ||
    get('postal_town') ||          // UK
    get('sublocality_level_1') ||
    get('sublocality') ||
    get('administrative_area_level_3') ||
    get('administrative_area_level_2');

  const zip         = get('postal_code');
  const countryCode = get('country', true);           // e.g. "US"
  const effectiveCountry = countryCode || countryHint;
  const state =
    effectiveCountry === 'US'
      ? get('administrative_area_level_1', true)      // "CA"
      : get('administrative_area_level_1', false);    // "Ontario"

  return { address1, city, state, zip, country: countryCode };
};

// LEGACY Geocoder format: long_name / short_name
const parseLegacyComponents = (components, countryHint) => {
  const get = (type, useShort = false) => {
    const c = components.find((x) => x.types.includes(type));
    return c ? (useShort ? c.short_name : c.long_name) : '';
  };

  const city =
    get('locality') ||
    get('postal_town') ||
    get('sublocality_level_1') ||
    get('sublocality') ||
    get('administrative_area_level_3') ||
    get('administrative_area_level_2');

  const zip         = get('postal_code');
  const countryCode = get('country', true);
  const effectiveCountry = countryCode || countryHint;
  const state =
    effectiveCountry === 'US'
      ? get('administrative_area_level_1', true)
      : get('administrative_area_level_1', false);

  return { city, state, zip, country: countryCode };
};

// ── Main hook ────────────────────────────────────────────────────────────────

export const useAddressAutocomplete = ({
  isLoaded,         // boolean — from useGoogleMapsLoader
  addressValue,     // formData.organizationAddress1
  country,          // formData.organizationCountry
  regionCodes,      // string[] — ISO-2 codes for the selected region (may be empty)
  zip,              // formData.organizationZip
  city,             // formData.organizationCity
  state,            // formData.organizationState
  onBatchChange,    // stable useCallback from Signup.js
}) => {
  const [suggestions, setSuggestions]         = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [zipLoading, setZipLoading]           = useState(false);
  const sessionTokenRef  = useRef(null);
  // Tracks the timestamp of the most recent suggestion selection.
  // Effect 1 suppresses the dropdown for 1.5 s after a selection because
  // handleSuggestionSelect calls onBatchChange twice (immediate fill + async
  // fetchFields), both of which change addressValue and would re-trigger the
  // effect — causing the dropdown to reopen right after it was closed.
  const lastSelectionRef = useRef(0);

  // ── Effect 1: address suggestions (300 ms debounce) ─────────────────────
  useEffect(() => {
    if (!isLoaded || !addressValue || addressValue.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    let cancelled = false;

    const timer = setTimeout(async () => {
      try {
        const { AutocompleteSuggestion, AutocompleteSessionToken } =
          await window.google.maps.importLibrary('places');

        if (cancelled) return;

        if (!sessionTokenRef.current) {
          sessionTokenRef.current = new AutocompleteSessionToken();
        }

        // Only fields supported by AutocompleteSuggestionRequest:
        //   input, sessionToken, includedRegionCodes, language,
        //   locationBias, locationRestriction, origin.
        // DO NOT add `types` (legacy) or `includedPrimaryTypes` (POI types only).
        const request = {
          input:        addressValue,
          sessionToken: sessionTokenRef.current,
        };

        // Priority: specific country > region codes > no restriction
        if (country && country.length === 2) {
          request.includedRegionCodes = [country.toLowerCase()];
        } else if (regionCodes && regionCodes.length > 0) {
          // Restrict to region's countries when no single country is chosen.
          // Google allows up to 15 codes; slice to stay within the limit.
          request.includedRegionCodes = regionCodes.slice(0, 15).map((c) => c.toLowerCase());
        }

        const { suggestions: raw } =
          await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

        if (!cancelled) {
          // Suppress reopening within 1.5 s of a selection — covers both the
          // immediate-fill trigger and the async fetchFields trigger.
          if (Date.now() - lastSelectionRef.current < 1500) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
          }

          // CRITICAL FIX: filter to place-only suggestions.
          // queryPredictions have placePrediction === null; calling .toPlace()
          // on null throws, which is caught silently, leaving all fields empty.
          const placeSuggestions = (raw || []).filter(
            (s) => s.placePrediction !== null && s.placePrediction !== undefined
          );
          setSuggestions(placeSuggestions);
          setShowSuggestions(placeSuggestions.length > 0);
        }
      } catch (err) {
        if (!cancelled) {
          setSuggestions([]);
        }
      }
    }, 300);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [addressValue, country, regionCodes, isLoaded]);

  // ── handleSuggestionSelect ───────────────────────────────────────────────
  const handleSuggestionSelect = useCallback(
    async (suggestion) => {
      // Stamp the selection time FIRST so Effect 1's guard fires before the
      // two onBatchChange calls below change addressValue and re-trigger it.
      lastSelectionRef.current = Date.now();
      setShowSuggestions(false);
      setSuggestions([]);

      // Guard: only process place suggestions (queryPredictions have null here)
      if (!suggestion?.placePrediction) return;

      // ── Step 1: fill address1 IMMEDIATELY from the suggestion text ─────────
      // This gives instant feedback before the async fetchFields call completes.
      // If fetchFields succeeds, it will overwrite with the corrected full value.
      const immediateAddress = suggestion.placePrediction.mainText?.text || '';
      if (immediateAddress) {
        onBatchChange({ organizationAddress1: immediateAddress });
      }

      // ── Step 2: fetch full address components for city/state/zip/country ──
      try {
        const place = suggestion.placePrediction.toPlace();
        await place.fetchFields({ fields: ['addressComponents', 'formattedAddress'] });

        const batch = {};

        if (place.addressComponents && place.addressComponents.length > 0) {
          const f = parseNewComponents(place.addressComponents, country);

          // Address line: prefer parsed street; fall back to formattedAddress
          if (f.address1) {
            batch.organizationAddress1 = f.address1;
          } else if (place.formattedAddress) {
            batch.organizationAddress1 = place.formattedAddress;
          }

          if (f.city)    batch.organizationCity    = f.city;
          if (f.state)   batch.organizationState   = f.state;
          if (f.zip)     batch.organizationZip     = f.zip;
          if (f.country) batch.organizationCountry = f.country;

          // ── ZIP fallback (two layers) ──────────────────────────────────────
          // The Places API (New) frequently omits postal_code from
          // addressComponents.  We try two approaches in order:
          //
          //  1. Geocode by place.id — unambiguous; returns street-level detail
          //     including postal_code far more reliably than a text query.
          //     place.id is always populated by toPlace() so no null risk.
          //
          //  2. Regex on formattedAddress — the formatted string typically
          //     embeds the postal code (e.g. "…, IL 60601, USA").  Handles
          //     US 5-digit (and ZIP+4) codes as a last resort.
          if (!f.zip) {
            // Layer 1 — placeId geocode
            if (place.id) {
              try {
                const geocoder  = new window.google.maps.Geocoder();
                const geoResult = await geocoder.geocode({ placeId: place.id });
                if (geoResult.results?.length > 0) {
                  const legacy = parseLegacyComponents(
                    geoResult.results[0].address_components,
                    f.country || country
                  );
                  if (legacy.zip) batch.organizationZip = legacy.zip;
                }
              } catch (err) {}
            }

            // Layer 2 — regex on formattedAddress
            if (!batch.organizationZip && place.formattedAddress) {
              const m = place.formattedAddress.match(/\b(\d{5}(?:-\d{4})?)\b/);
              if (m) batch.organizationZip = m[1];
            }
          }
        } else if (place.formattedAddress) {
          // addressComponents empty but formattedAddress available — use it
          batch.organizationAddress1 = place.formattedAddress;
        }

        if (Object.keys(batch).length > 0) {
          onBatchChange(batch);
        }
      } catch (err) {} finally {
        sessionTokenRef.current = null;
      }
    },
    [country, onBatchChange]
  );

  // ── Effect 2: ZIP → city + state (400 ms debounce, ≥4 digits) ───────────
  useEffect(() => {
    if (!isLoaded || !zip || zip.replace(/\D/g, '').length < 4) return;

    let cancelled = false;

    const timer = setTimeout(async () => {
      setZipLoading(true);
      try {
        const geocoder = new window.google.maps.Geocoder();
        const req      = { address: zip };
        if (country && country.length === 2) {
          req.componentRestrictions = { country: country.toLowerCase() };
        }

        const result = await geocoder.geocode(req);

        if (!cancelled && result.results?.length > 0) {
          const p = parseLegacyComponents(result.results[0].address_components, country);
          const updates = {};
          if (p.city)  updates.organizationCity  = p.city;
          if (p.state) updates.organizationState = p.state;
          if (Object.keys(updates).length > 0) onBatchChange(updates);
        }
      } catch (err) {} finally {
        if (!cancelled) setZipLoading(false);
      }
    }, 400);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [zip, country, isLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Effect 3: city → state (600 ms debounce) ────────────────────────────
  // NOTE: ZIP is intentionally NOT filled here.
  // Geocoding a city name returns a locality with no postal_code component
  // (a city spans dozens of ZIP codes — there is no single correct answer).
  // `zip` and `state` are also intentionally NOT in the dep array to prevent
  // the effect from re-triggering after it just wrote state, which would loop.
  useEffect(() => {
    if (!isLoaded || !city || city.length < 3 || state) return;

    let cancelled = false;

    const timer = setTimeout(async () => {
      try {
        const geocoder = new window.google.maps.Geocoder();
        const query    = country ? `${city}, ${country}` : city;
        const result   = await geocoder.geocode({ address: query });

        if (!cancelled && result.results?.length > 0) {
          const p = parseLegacyComponents(result.results[0].address_components, country);
          if (p.state) onBatchChange({ organizationState: p.state });
        }
      } catch (err) {}
    }, 600);

    return () => { cancelled = true; clearTimeout(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city, country, isLoaded]);

  // ── validateAddress ──────────────────────────────────────────────────────
  const validateAddress = useCallback(
    async (formData) => {
      if (!isLoaded) return { isValid: true, errors: {} };

      const parts = [
        formData.organizationAddress1,
        formData.organizationCity,
        formData.organizationState,
        formData.organizationZip,
        formData.organizationCountry,
      ].filter(Boolean);

      if (parts.length < 3) return { isValid: true, errors: {} };

      try {
        const geocoder = new window.google.maps.Geocoder();
        const result   = await geocoder.geocode({ address: parts.join(', ') });

        if (!result.results?.length) {
          return {
            isValid: false,
            errors: { organizationAddress1: 'Address could not be verified. Please check your entries.' },
          };
        }
        return { isValid: true, errors: {} };
      } catch {
        return { isValid: true, errors: {} }; // fail-open
      }
    },
    [isLoaded]
  );

  return {
    suggestions,
    showSuggestions,
    setShowSuggestions,
    handleSuggestionSelect,
    zipLoading,
    validateAddress,
  };
};
